(() => {
  'use strict';
  if (customElements.get('lv-product')) return;
  class LvProduct extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;
      this.abort = new AbortController();
      const options = { signal: this.abort.signal };
      this.variants = JSON.parse(this.querySelector('[data-variants]').textContent);
      this.form = this.querySelector('form');
      this.track = this.querySelector('.lv-product__track');
      this.slides = [...this.querySelectorAll('.lv-product__slide')];
      this.dialog = this.querySelector('dialog');
      this.index = 0;
      this.addEventListener('click', event => {
        const quantity = event.target.closest('[data-quantity-step]');
        const step = event.target.closest('[data-gallery-step]');
        const thumb = event.target.closest('[data-gallery-index]');
        const zoom = event.target.closest('[data-zoom]');
        if (quantity) this.setQuantity(Number(this.form.elements.quantity.value) + Number(quantity.dataset.quantityStep));
        if (step) this.go(this.index + Number(step.dataset.galleryStep));
        if (thumb) this.go(Number(thumb.dataset.galleryIndex));
        if (zoom && !zoom.disabled && this.dialog) {
          this.index = Number(zoom.dataset.zoom);
          this.updateZoom();
          this.dialog.showModal();
        }
        if (event.target.closest('[data-zoom-close]')) this.dialog.close();
      }, options);
      this.addEventListener('change', event => {
        if (event.target.matches('input[type="radio"]')) this.selectVariant(event.target.value, true);
        if (event.target.name === 'quantity') this.setQuantity(event.target.value);
      }, options);
      this.track.addEventListener('scroll', () => {
        const next = Math.round(this.track.scrollLeft / Math.max(1, this.track.clientWidth));
        if (next !== this.index) { this.index = next; this.updateGallery(); }
      }, { ...options, passive: true });
      this.addEventListener('keydown', event => {
        if (!(this.dialog?.open || this.track.contains(event.target))) return;
        if (event.key === 'ArrowRight') { event.preventDefault(); this.go(this.index + 1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); this.go(this.index - 1); }
      }, options);
      this.form.addEventListener('submit', event => { event.preventDefault(); this.addToCart(); }, options);
      this.resize = new ResizeObserver(() => this.go(this.index, false));
      this.resize.observe(this.track);
      const mainButton = this.querySelector('[data-add]');
      const sticky = this.querySelector('.lv-product__sticky');
      if (sticky) {
        const updateSticky = () => { sticky.hidden = mainButton.getBoundingClientRect().bottom > 0; };
        this.observer = new IntersectionObserver(() => updateSticky());
        this.observer.observe(mainButton);
        window.addEventListener('scroll', updateSticky, { ...options, passive: true });
        document.addEventListener('scroll', updateSticky, { ...options, passive: true, capture: true });
        updateSticky();
      }
      this.selectVariant(this.form.elements.id.value, false);
      this.setQuantity(this.form.elements.quantity.value);
    }
    disconnectedCallback() { this.abort?.abort(); this.resize?.disconnect(); this.observer?.disconnect(); this.ready = false; }
    money(cents) {
      try { return new Intl.NumberFormat(this.dataset.locale || 'en', { style: 'currency', currency: this.dataset.currency || 'USD', currencyDisplay: 'narrowSymbol' }).format(cents / 100); }
      catch { return (cents / 100).toFixed(2); }
    }
    selectVariant(id, updateUrl) {
      const variant = this.variants.find(item => String(item.id) === String(id));
      if (!variant) return;
      this.variant = variant;
      this.form.elements.id.value = variant.id;
      this.querySelectorAll('input[type="radio"]').forEach(input => { input.checked = String(input.value) === String(id); });
      this.querySelector('[data-price]').textContent = this.money(variant.price);
      const compare = this.querySelector('[data-compare]');
      compare.hidden = !(variant.compare_at_price > variant.price);
      this.querySelector('[data-compare-value]').textContent = this.money(variant.compare_at_price || 0);
      this.querySelector('[data-variant-title]').textContent = variant.title;
      const button = this.querySelector('[data-add]');
      button.disabled = !variant.available;
      button.textContent = variant.available ? button.dataset.label : 'Sold out';
      this.querySelectorAll('[data-sticky-variant]').forEach(n => { n.textContent = variant.title; });
      this.querySelectorAll('[data-sticky-price]').forEach(n => { n.textContent = this.money(variant.price); });
      this.querySelectorAll('[data-sticky-add]').forEach(n => { n.disabled = !variant.available; });
      if (updateUrl) {
        const url = new URL(location.href);
        url.searchParams.set('variant', variant.id);
        history.replaceState({}, '', url);
      }
      this.dispatchEvent(new CustomEvent('lv:variant-change', { bubbles: true, detail: { variant } }));
    }
    setQuantity(value) {
      const quantity = Math.max(1, Math.floor(Number(value) || 1));
      this.form.elements.quantity.value = quantity;
      this.querySelector('[data-quantity-step="-1"]').disabled = quantity <= 1;
      this.querySelectorAll('[data-sticky-quantity]').forEach(n => { n.textContent = quantity; });
      return quantity;
    }
    go(index, animate = true) {
      this.index = (index + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: animate && !matchMedia('(prefers-reduced-motion: reduce)').matches ? 'smooth' : 'instant' });
      this.updateGallery();
    }
    updateGallery() {
      this.querySelectorAll('[data-gallery-index]').forEach(button => button.setAttribute('aria-current', String(Number(button.dataset.galleryIndex) === this.index)));
      if (this.dialog?.open) this.updateZoom();
    }
    updateZoom() {
      const source = this.slides[this.index]?.querySelector('img');
      if (!source) return;
      const image = this.querySelector('[data-zoom-image]');
      image.src = source.currentSrc || source.src;
      image.alt = source.alt || 'Product image ' + (this.index + 1);
    }
    async addToCart() {
      if (this.busy || !this.variant?.available) return;
      const quantity = this.setQuantity(this.form.elements.quantity.value);
      this.busy = true;
      const buttons = [...this.querySelectorAll('[data-add],[data-sticky-add]')];
      buttons.forEach(button => { button.disabled = true; button.setAttribute('aria-busy', 'true'); });
      const error = this.querySelector('[data-error]');
      error.hidden = true;
      try {
        const root = (window.Shopify?.routes?.root || this.dataset.root || '/').replace(/\/?$/, '/');
        const response = await fetch(root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items: [{ id: this.variant.id, quantity }] }) });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || 'Unable to add this item. Please try again.');
        location.assign(root + 'cart');
      } catch (failure) {
        error.textContent = failure.message || 'Unable to add this item. Please try again.';
        error.hidden = false;
      } finally {
        this.busy = false;
        buttons.forEach(button => { button.disabled = !this.variant.available; button.removeAttribute('aria-busy'); });
      }
    }
  }
  customElements.define('lv-product', LvProduct);
})();
